<template>
  <div class="w-full h-full">
    <AlertDialog :open="showFormModal" @update:open="(o) => !o && (showFormModal = false)">
      <AlertDialogContent class="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Edit timeframe</AlertDialogTitle>
          <AlertDialogDescription>Edit analytics timeframe</AlertDialogDescription>
        </AlertDialogHeader>
        <div class="space-y-3">
          <div>
            <label class="text-sm font-medium" for="form-start">Start date</label>
            <Input id="form-start" v-model="formStart" type="date" />
          </div>
          <div>
            <label class="text-sm font-medium" for="form-end">End date</label>
            <Input id="form-end" v-model="formEnd" type="date" />
          </div>
        </div>
        <AlertDialogFooter class="gap-2">
          <AlertDialogCancel @click="showFormModal = false">Cancel</AlertDialogCancel>
          <AlertDialogAction
            :disabled="!(formStart != '' && formEnd != '')"
            @click="updateAnalytics(); showFormModal = false"
          >Apply</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <div class="flex items-center gap-3 mb-4">
      <Telescope class="w-7 h-7 text-foreground" />
      <h1 class="text-2xl font-semibold tracking-tight">Spend explorer</h1>
    </div>
    <div v-if="!finishedloading" class="space-y-4">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Skeleton v-for="i in 4" :key="`s${i}`" class="h-20" />
      </div>
      <Skeleton class="h-48" />
      <div class="space-y-2">
        <Skeleton v-for="i in 5" :key="`t${i}`" class="h-10" />
      </div>
    </div>
    <section v-if="finishedloading" class="mb-4">
      <div class="flex items-baseline justify-between mb-3">
        <div>
          <h2 class="text-lg font-semibold">Merchant Spend Analysis</h2>
          <p class="text-sm text-muted-foreground">View your customers spending behaviour at merchants between {{ niceDateTime(startDate) }} to {{ niceDateTime(endDate) }}</p>
        </div>
        <div>
          <Button size="sm" id="createContract" @click="showFormModal = true">
            <CalendarDays class="w-4 h-4" />Edit timeframe
          </Button>
        </div>
      </div>
      <div v-if="topLineData != undefined && retailers.length > 0" class="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card class="p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs uppercase tracking-wide text-muted-foreground">Spend</p>
            <CreditCard class="w-4 h-4 text-muted-foreground" />
          </div>
          <p class="text-2xl font-semibold">{{ fmt(Math.abs(topLineData.spendVolume), { decimals: 2, currency: '£' }) }}</p>
        </Card>
        <Card class="p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs uppercase tracking-wide text-muted-foreground">Transactions</p>
            <CreditCard class="w-4 h-4 text-muted-foreground" />
          </div>
          <p class="text-2xl font-semibold">{{ fmt(Math.abs(topLineData.spendFrequency), { decimals: 0 }) }}</p>
        </Card>
        <Card class="p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs uppercase tracking-wide text-muted-foreground">Top category by spend</p>
            <PieChart class="w-4 h-4 text-muted-foreground" />
          </div>
          <p class="text-2xl font-semibold">{{ topLineData.topCategory }}</p>
        </Card>
        <Card class="p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs uppercase tracking-wide text-muted-foreground">Top retailer by spend</p>
            <Store class="w-4 h-4 text-muted-foreground" />
          </div>
          <p class="text-2xl font-semibold">{{ retailers[0].retailer.retailerName }}</p>
        </Card>
      </div>
    </section>

    <div v-if="finishedloading" class="grid grid-cols-1 lg:grid-cols-2 gap-x-5">
      <div>
        <Card>
          <CardHeader><CardTitle>Share of spend</CardTitle></CardHeader>
          <CardContent>
            <div>
              <Doughnut v-if="chartData != undefined" :data="chartData" :options="chartOptions" />
            </div>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader><CardTitle>Categories</CardTitle></CardHeader>
        <CardContent>
          <DataTable
            :headers="headersTable2"
            :items="categorySOW"
            :loading="!finishedloading"
            :error="error"
            @retry="getCategories()"
            v-model:server-options="serverOptions"
            :server-items-length="serverItemsLength"
            :hide-footer="true"
          >
            <template #item-transactions="{ transactionCount }">
              <div class="text-right tabular-nums">{{ fmt(Math.abs(transactionCount), { decimals: 0 }) }}</div>
            </template>
            <template #item-spend="{ transactionVolume }">
              <div class="text-right tabular-nums">{{ fmt(Math.abs(transactionVolume), { decimals: 2, currency: '£' }) }}</div>
            </template>

            <template #item-action="retailers">
              <div class="flex justify-end items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  @click="
                    $router.push(
                      `/spend-explorer/${retailers.label.replaceAll(' ', '-').toLowerCase()}?start=${formStart}&end=${formEnd}`
                    )
                  "
                >View category</Button>
              </div>
            </template>
          </DataTable>
        </CardContent>
      </Card>
    </div>

    <Card v-if="finishedloading">
      <CardHeader>
        <CardTitle>Top retailers</CardTitle>
        <CardDescription>View the top retailers your customer spends with between {{ niceDateTime(startDate) }} to {{ niceDateTime(endDate) }}</CardDescription>
      </CardHeader>
      <CardContent>
        <div class="flex items-center gap-2 mb-4">
          <Input v-model="retailerSearchQuery" placeholder="Search by retailer name" @keyup.enter="getRetailerBySearch(0, 10, retailerSearchQuery)" />
          <Button size="sm" variant="outline" @click="getRetailerBySearch(0, 10, retailerSearchQuery)"><SearchIcon class="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" @click="getRetailers(0, 50)"><X class="w-4 h-4" /></Button>
        </div>
        <DataTable
          :headers="headersTable1"
          :items="retailers"
          :loading="!finishedloading || loadingSearch"
          :error="error"
          @retry="getRetailers(serverOptions.page - 1, serverOptions.rowsPerPage)"
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
          :hide-footer="true"
        >
          <template #item-retailer="retailers">
            <div class="flex items-center gap-2">
              <img v-if="retailers.retailer.logo" :src="retailers.retailer.logo" class="w-6 h-6 rounded object-contain border bg-white" alt="" />
              <span class="text-sm font-medium truncate">{{ retailers.retailer.retailerName }}</span>
            </div>
          </template>
          <template #item-transactions="{ transactionCount }">
            <div class="text-right tabular-nums">{{ fmt(Math.abs(transactionCount), { decimals: 0 }) }}</div>
          </template>
          <template #item-spend="{ transactionVolume }">
            <div class="text-right tabular-nums">{{ fmt(Math.abs(transactionVolume), { decimals: 2, currency: '£' }) }}</div>
          </template>
          <template #item-action="retailers">
            <div class="flex justify-end items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                @click="
                  $router.push(
                    `/retailer/${retailers.retailer.id}?start=${formStart}&end=${formEnd}`
                  )
                "
              >View retailer</Button>
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script setup></script>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Skeleton,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
  Input,
} from '@boogi/ui'
import { Telescope, CalendarDays, CreditCard, PieChart, Store, X, Search as SearchIcon } from 'lucide-vue-next'
import { DateTime } from 'luxon'
import { Doughnut } from 'vue-chartjs'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'

ChartJS.register(ArcElement, Tooltip, Legend)

import { DataTable } from '@boogi/ui'

export default {
  name: 'spend-explorer',
  components: {
    DataTable,
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    Button,
    Skeleton,
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
    Input,
    Telescope,
    CalendarDays,
    CreditCard,
    PieChart,
    Store,
    X,
    SearchIcon,
    Doughnut,
  },
  props: {},

  data() {
    return {
      serverOptions: {
        page: 1,
        rowsPerPage: 50,
      },
      topLineData: undefined,

      chartData: undefined,

      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'left',
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                const value = context.raw // Get the value
                return `${context.label}: ${value}%` // Add the % sign
              },
            },
          },
          title: {
            display: false,
            text: 'Transaction count',
          },
        },
      },
      categorySOW: [],

      serverItemsLength: undefined,
      startDate: '',
      endDate: '',
      formStart: '',
      formEnd: '',
      category: '',
      options: [
        { id: 'Other%20non-essential', text: 'Other non-essential' },
        { id: 'Cafes%20and%20coffee%20shops', text: 'Cafes and coffee shops' },
        { id: 'Pets', text: 'Pets' },
        { id: 'Books%2C%20newsletters%2C%20newspapers', text: 'Books, newsletters, newspapers' },
        { id: 'Food%20and%20Drink', text: 'Food and Drink' },
        { id: 'SaaS%20tools', text: 'SaaS tools' },
        { id: 'Other%20consumer%20services', text: 'Other consumer services' },
        { id: 'Insurance', text: 'Insurance' },
        { id: 'Ridesharing%20and%20taxis', text: 'Ridesharing and taxis' },
        { id: 'Other%20transport', text: 'Other transport' },
        { id: 'Convenience%20stores', text: 'Convenience stores' },
        { id: 'Laundry', text: 'Laundry' },
        { id: 'Groceries', text: 'Groceries' },
        { id: 'Clothing', text: 'Clothing' },
        { id: 'Media', text: 'Media' },
        { id: 'Drugstores%20and%20pharmacies', text: 'Drugstores and pharmacies' },
        { id: 'eCommerce%20purchase', text: 'eCommerce purchase' },
        { id: 'Education', text: 'Education' },
        {
          id: 'Home%20improvements%20and%20maintenance%20services',
          text: 'Home improvements and maintenance services',
        },
        { id: 'Self%20care', text: 'Self care' },
        { id: 'Vehicle%20maintenance', text: 'Vehicle maintenance' },
        { id: 'Taxes', text: 'Taxes' },
        { id: 'Buy%20now%2C%20pay%20later', text: 'Buy now, pay later' },
        { id: 'Medical%20bill', text: 'Medical bill' },
        { id: 'Sport%20and%20fitness', text: 'Sport and fitness' },
        { id: 'Electronics', text: 'Electronics' },
        { id: 'Entertainment%20and%20recreation', text: 'Entertainment and recreation' },
        { id: 'Liquor', text: 'Liquor' },
        { id: 'Utilities', text: 'Utilities' },
        { id: 'Department%20and%20discount%20stores', text: 'Department and discount stores' },
        { id: 'Hotels%20and%20lodging', text: 'Hotels and lodging' },
        { id: 'Gifts', text: 'Gifts' },
        { id: 'Recreational%20goods', text: 'Recreational goods' },
        { id: 'Donation', text: 'Donation' },
      ],

      showErrorModal: false,
      showSuccessModal: false,
      showFormModal: false,
      loading: true,
      error: false,
      loadingSearch: false,
      loadingCategories: true,
      loadingRetailers: true,
      retailerSearchQuery: '',
      headersTable1: [
        { text: 'Retailer', value: 'retailer' },
        { text: 'Category', value: 'retailer.personalisationCategories' },
        { text: 'Transactions', value: 'transactions', align: 'right' },
        { text: 'Spend', value: 'spend', align: 'right' },
        { text: 'Action', value: 'action' },
      ],
      headersTable2: [
        { text: 'Category', value: 'label' },
        { text: 'Transactions', value: 'transactions', align: 'right' },
        { text: 'Spend', value: 'spend', align: 'right' },
        { text: 'Action', value: 'action' },
      ],
      retailers: [],
    }
  },
  created() {
    if (this.$route.query.start && this.$route.query.end) {
      this.formStart = this.$route.query.start
      this.formEnd = this.$route.query.end
      this.startDate = DateTime.fromFormat(this.formStart, 'dd-mm-yyyy').startOf('day')
      this.endDate = DateTime.fromFormat(this.formEnd, 'dd-mm-yyyy').endOf('day')
    } else {
      const now = DateTime.local()
      const startOfMonth = now.startOf('month')
      const endOfMonth = now.endOf('month')

      this.startDate = startOfMonth.minus({ month: 2 })
      this.endDate = endOfMonth
      this.formStart = this.startDate.toFormat('dd-MM-yyyy')
      this.formEnd = this.endDate.toFormat('dd-MM-yyyy')
    }
    this.getRetailers(0, 50)
    this.getCategories()
  },
  methods: {
    fmt(n, opts = {}) {
      const { decimals = 2, currency = '', percentage = false } = opts
      const num = Number(n) || 0
      const formatted = num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
      return `${currency}${formatted}${percentage ? '%' : ''}`
    },
    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATE_SHORT)
    },

    updateAnalytics() {
      this.startDate = DateTime.fromISO(this.formStart).startOf('day')
      this.endDate = DateTime.fromISO(this.formEnd).endOf('day')
      this.formStart = this.startDate.toFormat('dd-MM-yyyy')
      this.formEnd = this.endDate.toFormat('dd-MM-yyyy')
      this.getRetailers(0, 50)
      this.getCategories()
    },

    async getRetailerBySearch(page, size, search) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loadingSearch = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/share-of-wallet/retailer/popularity/search?page=${page}&size=${size}&startDate=${this.formStart}&endDate=${this.formEnd}&search=${search}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.serverItemsLength = object.length
          this.retailers = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loadingSearch = false
      }
    },

    async getRetailers(page, size) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loadingSearch = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/share-of-wallet/retailer/popularity?page=${page}&size=${size}&startDate=${this.formStart}&endDate=${this.formEnd}${this.category == '' ? '' : '&categories=' + this.category}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.serverItemsLength = object.length
          this.retailers = object
          this.loadingRetailers = false
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loadingSearch = false
      }
    },

    async getCategories() {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loadingCategories = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/share-of-wallet/category?startDate=${this.formStart}&endDate=${this.formEnd}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.serverItemsLength = object.length
          this.categorySOW = object

          this.topLineData = {
            spendVolume: object.reduce((sum, item) => sum + item.transactionVolume, 0),
            spendFrequency: object.reduce((sum, item) => sum + item.transactionCount, 0),
            topCategory: object[0].label,
          }

          this.chartData = {
            labels: object.map((item) => item.label),
            datasets: [
              {
                data: object.map((item) =>
                  Math.abs((item.transactionVolume / this.topLineData.spendVolume) * 100).toFixed(2)
                ),
              },
            ],
          }
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loadingCategories = false
      }
    },
  },
  computed: {
    finishedloading() {
      return this.loadingCategories == false && this.loadingRetailers == false
    },
  },
  mounted() {
    document.title = 'Dashboard | Spend explorer'
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getRetailers(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
